import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Organization } from 'ui/types/interfaces/organization';
import { isBase64Image, isUrlImage } from 'ui/utils/validations/url';

import { ECOSYSTEM_MESSAGES } from '~/constants/messages.constants';
import { PAGES } from '~/constants/pages.constants';
import { EcosystemVisibility, OrgPlusContentViewedFlags } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useQuery } from '~/hooks/useQuery';
import { useRouter } from '~/hooks/useRouter';
import { EcosystemService } from '~/services/resources/ecosystem';
import { OrganizationService } from '~/services/resources/organization';
import { switchEcosystemAction } from '~/store/common/actions/ecosystem/switchEcosystem';
import { IPaginatedResponse } from '~/types';
import { IEcosystem } from '~/types/interfaces/ecosystem';
import { IOrganization } from '~/types/interfaces/organization';
import { hasSomeAdminEditedEcosystem } from '~/utils/hasSomeAdminEditedEcosystem';
import { uploadImageFromBase64 } from '~/utils/uploadLogoIfProvided';

import { ICreateEcosystemForm } from '../types';

export const useEditEcosystemController = () => {
  const { params, replaceRoute } = useRouter();
  const { organizationSelectedId } = useAppSelector(({ auth }) => auth);
  const { selectedEcosystem } = useAppSelector(({ ecosystem }) => ecosystem);

  const dispatch = useAppDispatch();

  const { data: organization } = useQuery<IOrganization>(
    `organization/${organizationSelectedId}`,
  );
  const { data: ecosystems } = useQuery<IPaginatedResponse<IEcosystem[]>>(
    'ecosystem',
    {
      requestOptions: {
        params: {
          filter: JSON.stringify({ organization: organizationSelectedId }),
          limit: 1,
        },
      },
      queryOptions: {
        enabled: !!organization && !hasSomeAdminEditedEcosystem(organization),
      },
    },
  );

  const queryClient = useQueryClient();
  const ecosystemIdToEdit = params.id;

  const handleOnSubmit = async (ecosystemData: ICreateEcosystemForm) => {
    if (!ecosystemIdToEdit) return;

    const logo = ecosystemData?.logo || '';
    let logoURL;

    if (isBase64Image(logo)) {
      logoURL = await uploadImageFromBase64(logo);
    } else if (isUrlImage(logo)) {
      logoURL = logo;
    }

    try {
      if (
        ecosystems?.count === 1 &&
        organization &&
        !hasSomeAdminEditedEcosystem(organization)
      ) {
        let contentViewedFlags = 0;
        if (organization.contentViewedFlags) {
          contentViewedFlags =
            organization.contentViewedFlags |
            OrgPlusContentViewedFlags.firstEditEcosystemModal;
        } else {
          contentViewedFlags =
            OrgPlusContentViewedFlags.firstEditEcosystemModal;
        }

        /* NOTE: Api nao suporta contentViewedFlags e aparentemente nao tem utilidade */
        await OrganizationService.updateOne(organization._id, {
          contentViewedFlags,
        } as Partial<Organization>);
      }

      await EcosystemService.updateOne(ecosystemIdToEdit, {
        ...ecosystemData,
        visibility: ecosystemData.isPrivate
          ? EcosystemVisibility.Private
          : EcosystemVisibility.Public,
        logo: logoURL,
        causeOptions: ecosystemData.causeOptions.map((option) => option.value),
        measurementUnits: ecosystemData.measurementUnits.map(
          (unit) => unit.value,
        ),
      });

      if (ecosystemIdToEdit === selectedEcosystem?._id) {
        await dispatch(
          switchEcosystemAction({
            organizationSelectedId,
            ecosystemId: ecosystemIdToEdit,
          }),
        );
      }

      await queryClient.invalidateQueries(`/ecosystem/${ecosystemIdToEdit}`);
      await queryClient.invalidateQueries('activity-category');
      toast.success(ECOSYSTEM_MESSAGES.SUCCESS_ON_UPDATE);
      replaceRoute(`${PAGES.ECOSYSTEM_SETTINGS}/${ecosystemIdToEdit}`);
    } catch {
      toast.error(ECOSYSTEM_MESSAGES.ERROR_ON_UPDATE);
    }
  };

  return {
    ecosystemIdToEdit,
    handleOnSubmit,
  };
};
